import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { FilterToggleButton } from './FilterToggleButton';
import { SearchableSelect } from './SearchableSelect';
import {
  useGetDailySalesListQuery,
  useImportDailySalesCsvMutation,
} from '../../redux/dailySalesApi';
import { useGetActiveLocationsQuery } from '../../redux/employeeApi';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import {
  formatSaleDate,
  listDailySalesFromResponse,
  mapDailySaleRow,
} from '../../utils/dailySalesList';

const LIST_COLUMNS = [
  'Id',
  'Reference Number',
  'Bill Date',
  'Location',
  'Sale Items',
  'Guest Count',
  'Total Amount',
  'Action',
];

const EMPTY_FILTERS = {
  location_id: '',
  bill_date: '',
};

const filterFieldClass =
  'mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function AdminDailySales() {
  const fileInputRef = useRef(null);
  const [importDailySalesCsv, { isLoading: isImporting }] = useImportDailySalesCsvMutation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const pageSize = DEFAULT_PAGE_SIZE;

  const { data: locationsRes } = useGetActiveLocationsQuery();

  const locationOptions = useMemo(() => {
    const list = locationsRes?.data || locationsRes || [];
    return (Array.isArray(list) ? list : [])
      .map((loc) => {
        const id = loc?.id ?? loc?.location_id;
        const label = String(loc?.name || loc?.location_name || '').trim();
        if (id == null || id === '') return null;
        return {
          value: String(id),
          label: label || String(id),
        };
      })
      .filter(Boolean);
  }, [locationsRes]);

  useEffect(() => {
    if (!isFilterOpen) return;
    setFilterDraft(filters);
  }, [isFilterOpen, filters]);

  const hasActiveFilters = Boolean(filters.location_id || filters.bill_date);

  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.bill_date) params.bill_date = filters.bill_date;
    return params;
  }, [page, pageSize, search, filters]);

  const { data: salesRes, isLoading, isFetching, error, refetch } =
    useGetDailySalesListQuery(listQuery);

  const rows = useMemo(
    () => listDailySalesFromResponse(salesRes).map(mapDailySaleRow).filter((row) => row.id != null),
    [salesRes]
  );

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(salesRes, {
    page,
    pageSize,
  });

  const handleFilterDraftChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFilterDraft((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters(filterDraft);
    setPage(1);
  };

  const resetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setIsFilterOpen(false);
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file.');
      return;
    }

    try {
      const result = await importDailySalesCsv(file).unwrap();
      toast.success(
        result?.message || result?.data?.message || 'CSV imported successfully.'
      );
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to import CSV.');
    }
  };

  return (
    <DashboardLayout title="Daily Sales">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search daily sales"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>

            <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end md:w-auto">
              <FilterToggleButton
                active={hasActiveFilters}
                onClick={() => setIsFilterOpen((prev) => !prev)}
                className="w-full sm:w-auto"
                ariaLabel="Toggle daily sales filters"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                disabled={isImporting}
                onChange={handleCsvUpload}
              />
              <button
                type="button"
                disabled={isImporting}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {isImporting ? 'Uploading…' : 'Upload CSV'}
              </button>
              <Link
                to="/dashboard/admin/build-to-flow/daily-sales/add"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Daily Sales
              </Link>
            </div>
          </div>

          {isFilterOpen ? (
            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col text-sm text-gray-700">
                Location
                <SearchableSelect
                  name="location_id"
                  value={filterDraft.location_id}
                  onChange={handleFilterDraftChange('location_id')}
                  options={locationOptions}
                  placeholder="All Locations"
                  clearable
                  className={filterFieldClass}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Bill Date
                <input
                  type="date"
                  name="bill_date"
                  value={filterDraft.bill_date}
                  onChange={handleFilterDraftChange('bill_date')}
                  className={filterFieldClass}
                />
              </label>
              <div className="flex items-end gap-2 sm:col-span-2">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
            </div>
          ) : null}

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={rows.length > 0}
            error={error}
            refetch={refetch}
            message="Loading daily sales…"
            className="py-6 lg:hidden"
          />
          {!error && (rows.length > 0 || (!isLoading && !isFetching)) && rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 lg:hidden">No daily sales found.</p>
          ) : !error && rows.length > 0 ? (
            <ul className="mb-4 space-y-3 lg:hidden">
              {rows.map((row) => (
                <li key={row.id} className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">#{row.id}</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {row.referenceNumber || '—'}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">Bill {row.billNo || '—'}</p>
                      <p className="mt-1 text-xs text-gray-600">{formatSaleDate(row.billDate)}</p>
                      <p className="mt-1 text-sm font-medium text-brand">{row.location || '—'}</p>
                      <p className="mt-1 text-xs text-gray-600">
                        Items: {row.itemsCount || 0} · Amount: {row.amount || '—'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Link
                        to={`/dashboard/admin/build-to-flow/daily-sales/${row.id}`}
                        state={{ sale: row }}
                        className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                        aria-label="View daily sale"
                        title="View"
                      >
                        <FaEye className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/dashboard/admin/build-to-flow/daily-sales/${row.id}/edit`}
                        state={{ sale: row }}
                        className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                        aria-label="Edit daily sale"
                        title="Edit"
                      >
                        <MdEdit className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  {LIST_COLUMNS.map((label) => (
                    <th
                      key={label}
                      className="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={LIST_COLUMNS.length}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading daily sales..."
                />
                {!error && !isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={LIST_COLUMNS.length} className="px-3 py-8 text-center text-sm text-gray-500">
                      No daily sales found.
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-amber-50/40">
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 text-gray-600">
                      {row.id}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2 font-medium text-gray-900">
                      {row.referenceNumber || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2">{formatSaleDate(row.billDate)}</td>
                    <td className="border-b border-gray-100 px-3 py-2 font-medium text-brand">
                      {row.location || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.itemsCount || 0}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.guestCount || '—'}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.amount || '—'}</td>
                    <td className="border-b border-gray-100 px-3 py-2">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/dashboard/admin/build-to-flow/daily-sales/${row.id}`}
                          state={{ sale: row }}
                          className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                          aria-label="View daily sale"
                          title="View"
                        >
                          <FaEye className="h-5 w-5" />
                        </Link>
                        <Link
                          to={`/dashboard/admin/build-to-flow/daily-sales/${row.id}/edit`}
                          state={{ sale: row }}
                          className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                          aria-label="Edit daily sale"
                          title="Edit"
                        >
                          <MdEdit className="h-5 w-5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminDailySales;
